import { EntityRocList } from "@modernary/mui/components/roc";

import { EntityAudit } from "@modernary/mui/components/audit";
import {
  EntityDocumentForm,
  EntityDocumentMultipleForm,
  EntityDocuments,
  EntityDocumentsChildList,
} from "@modernary/mui/components/document";
import { EntityNotes } from "@modernary/mui/components/note";
import { withProps } from "@modernary/mui/utils/helpers";
import { RouterView, type RouterOptions } from "vue-router";

// Troubleshoot your routes using this - https://paths.esm.dev

const names = {
  stationarySources: "StationarySource.StationarySources",
  list: "StationarySource.StationarySourceList",
  new: "StationarySource.StationarySourceNew",
  base: "StationarySource.StationarySourceBase",
  view: "StationarySource.StationarySourceView",
  edit: "StationarySource.StationarySourceEdit",
  users: "StationarySource.StationarySourceUsers",
  userNew: "StationarySource.StationarySourceUserNew",
  complaints: "StationarySource.StationarySourceComplaints",
  complaintInvestigationsChildList: "StationarySource.ComplaintInvestigationsChildList",
  enforcementActions: "StationarySource.StationarySourceEnforcementActions",
  documents: "StationarySource.StationarySourceDocuments",
  documentsEdit: "StationarySource.StationarySourceDocumentsEdit",
  documentsNew: "StationarySource.StationarySourceDocumentsNew",
  notes: "StationarySource.StationarySourceNotes",
  noteEdit: "StationarySource.StationarySourceNoteEdit",
  noteNew: "StationarySource.StationarySourceNoteNew",
  rocs: "StationarySource.StationarySourceRocs",
  rocEdit: "StationarySource.StationarySourceRocEdit",
  rocNew: "StationarySource.StationarySourceRocNew",
  audit: "StationarySource.StationarySourceAudit",
};

export { names as stationarySourceRouteNames };

const stationarySourceRoutes: RouterOptions["routes"] = [
  {
    path: "/stationarySource",
    name: names.stationarySources,
    component: {
      render: () => <RouterView />,
    },
    redirect: { name: names.list },
    children: [
      {
        path: "list",
        name: names.list,
        meta: { title: "Stationary Sources" },
        component: () => import("@/views/stationarySource/StationarySourceList.vue"),
      },
      {
        path: "new",
        component: () => import("@/views/stationarySource/StationarySourceBase.vue"),
        redirect: { name: names.new },
        props: true,
        children: [
          {
            path: "",
            name: names.new,
            meta: { title: "New Stationary Source", isNew: true },
            component: () => import("@/views/stationarySource/StationarySourceEdit.vue"),
          },
        ],
      },
      {
        path: ":id(\\d+)",
        name: names.base,
        component: () => import("@/views/stationarySource/StationarySourceBase.vue"),
        redirect: { name: names.view },
        children: [
          {
            path: "view",
            name: names.view,
            meta: { title: "Stationary Source View" },
            component: () => import("@/views/stationarySource/StationarySourceView.vue"),
          },
          {
            path: "edit",
            name: names.edit,
            meta: { title: "Stationary Source Edit", isForm: true },
            component: () => import("@/views/stationarySource/StationarySourceEdit.vue"),
          },
          {
            path: "users",
            name: names.users,
            meta: { title: "Stationary Source Users" },
            component: () => import("@/views/stationarySource/StationarySourceUsers.vue"),
          },
          {
            path: "users/new",
            name: names.userNew,
            meta: { title: "Add Stationary Source User", isForm: true },
            component: () => import("@/views/stationarySource/StationarySourceUser.vue"),
          },
          {
            path: "complaints",
            name: names.complaints,
            meta: { title: "Stationary Source Complaints" },
            component: () => import("@/views/stationarySource/ComplaintChildList.vue"),
          },
          {
            path: "investigations",
            name: names.complaintInvestigationsChildList,
            meta: { title: "Investigations" },
            component: () => import("@/views/stationarySource/InvestigationChildList.vue"),
          },
          {
            path: "enforcementActions",
            name: names.enforcementActions,
            meta: { title: "Source Enforcement Actions" },
            component: () => import("@/views/stationarySource/EnforcementActionChildList.vue"),
          },
          {
            path: "documents",
            name: names.documents,
            meta: { title: "Stationary Source Documents" },
            component: {
              render: () => <EntityDocumentsChildList resourceTypeKey="stationarySource" />,
            },
          },
          {
            path: "documents/:documentId(\\d+)",
            name: names.documentsEdit,
            meta: { title: "Edit Stationary Source Document", isForm: true },
            component: {
              render: () => <EntityDocumentForm resourceTypeKey="stationarySource" />,
            },
          },
          {
            path: "documents/new",
            name: names.documentsNew,
            meta: { title: "Create Stationary Source Documents", isForm: true },
            component: {
              render: () => <EntityDocumentMultipleForm resourceTypeKey="stationarySource" />,
            },
          },
          {
            path: "notes",
            name: names.notes,
            meta: { title: "Stationary Source Notes" },
            component: {
              render: () => (
                <EntityNotes idField="stationarySourceId" routeNamePrefix="StationarySource.StationarySourceNote" />
              ),
            },
          },
          {
            path: "notes/:annotationId(\\d+)",
            name: names.noteEdit,
            meta: { title: "Edit Stationary Source Note", isForm: true },
            component: () => import("@/views/stationarySource/StationarySourceNote.vue"),
          },
          {
            path: "notes/new",
            name: names.noteNew,
            meta: { title: "Create Stationary Source Note", isForm: true },
            component: () => import("@/views/stationarySource/StationarySourceNote.vue"),
          },
          {
            path: "activity",
            name: names.audit,
            meta: { title: "Stationary Source Audit Activity" },
            component: {
              render: () => <EntityAudit entityType="StationarySource" />,
            },
          },
          {
            path: "rocs",
            name: names.rocs,
            meta: { title: "StationarySource ROC" },
            component: {
              render: () => <EntityRocList entityResourceTypeKey="stationarySource" />,
            },
          },
          {
            path: "rocs/:entityRocId(\\d+)",
            name: names.rocEdit,
            meta: { title: "Edit StationarySource ROC", isForm: true },
            ...withProps(async () => (await import("@modernary/mui/components/roc")).EntityRocForm, {
              entityResourceTypeKey: "stationarySource",
              listName: names.rocs,
            }),
          },
          {
            path: "rocs/new",
            name: names.rocNew,
            meta: { title: "Create StationarySource ROC", isForm: true },
            ...withProps(async () => (await import("@modernary/mui/components/roc")).EntityRocForm, {
              entityResourceTypeKey: "stationarySource",
              listName: names.rocs,
            }),
          },
        ],
      },
    ],
  },
];
export default stationarySourceRoutes;
